import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import {
  AuthMethod,
  postV1SsoConnections,
  SSOConnection_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import FormWrapperBox from './FormWrapperBox';
import SignInMagicLinkSentScreen from './SignInMagicLinkSentScreen';
import SignInWithMagicLinkForm from './SignInWithMagicLinkForm';
import SignInWithPasswordForm from './SignInWithPasswordForm';
import SignInWithSSOForm from './SignInWithSSOForm';

interface SignInWithEmailFormProps {
  availableStrategies: AuthMethod[];
  email: string;
  onBack: () => void;
}

enum SignInWithEmailStep {
  SELECT_STRATEGY = 'select_strategy',
  MAGIC_LINK_SENT = 'magic_link_sent',
}

const SignInWithEmailForm = ({
  availableStrategies,
  email,
  onBack,
}: SignInWithEmailFormProps) => {
  const [currentStep, setCurrentStep] = useState<SignInWithEmailStep>(
    SignInWithEmailStep.SELECT_STRATEGY,
  );
  const [ssoConnections, setSsoConnections] = useState<SSOConnection_Read[]>([]);

  const isMagicLinkStrategyAvailable = useMemo(() => {
    return availableStrategies.includes(AuthMethod.MAGIC_LINK);
  }, [availableStrategies]);

  const isPasswordStrategyAvailable = useMemo(() => {
    return availableStrategies.includes(AuthMethod.PASSWORD);
  }, [availableStrategies]);

  const isSsoStrategyAvailable = useMemo(() => {
    return availableStrategies.includes(AuthMethod.SSO);
  }, [availableStrategies]);

  useEffect(() => {
    if (!isSsoStrategyAvailable || !email) return;
    postV1SsoConnections({ body: { email } })
      .then((resp) => setSsoConnections(resp.data.data))
      .catch(() => setSsoConnections([]));
  }, [isSsoStrategyAvailable, email]);

  const handleBackButtonClick = useCallback(() => {
    if (currentStep === SignInWithEmailStep.SELECT_STRATEGY) {
      onBack();
    } else if (currentStep === SignInWithEmailStep.MAGIC_LINK_SENT) {
      setCurrentStep(SignInWithEmailStep.SELECT_STRATEGY);
    }
  }, [currentStep, onBack]);

  const signInDivider = (
    <Divider flexItem sx={{ my: 3 }}>
      <Typography variant="body2">{t('common.or').toUpperCase()}</Typography>
    </Divider>
  );

  const signInOptions = useMemo(
    () =>
      [
        isPasswordStrategyAvailable && (
          <SignInWithPasswordForm key="password" email={email} showEmailField={true} />
        ),
        isMagicLinkStrategyAvailable && (
          <SignInWithMagicLinkForm
            key="magic_link"
            email={email}
            onMagicLinkSent={() => setCurrentStep(SignInWithEmailStep.MAGIC_LINK_SENT)}
          />
        ),
        isSsoStrategyAvailable && ssoConnections.length > 0 && (
          <SignInWithSSOForm key="sso" ssoConnections={ssoConnections} />
        ),
      ].filter(Boolean) as React.ReactElement[],
    [
      isPasswordStrategyAvailable,
      isMagicLinkStrategyAvailable,
      isSsoStrategyAvailable,
      ssoConnections,
      email,
    ],
  );

  return (
    <FormWrapperBox hideLogo onBack={handleBackButtonClick} sx={{ pt: 10 }}>
      {currentStep === SignInWithEmailStep.SELECT_STRATEGY &&
        signInOptions.map((option, index) => (
          <React.Fragment key={option.key}>
            {index > 0 && signInDivider}
            {option}
          </React.Fragment>
        ))}

      {currentStep === SignInWithEmailStep.MAGIC_LINK_SENT && (
        <SignInMagicLinkSentScreen email={email} />
      )}
    </FormWrapperBox>
  );
};

export default SignInWithEmailForm;
